import React, { useEffect, useState } from 'react';
import { Loader2, Clock, Sparkles } from 'lucide-react';
import { api } from '../contexts/AuthContext';

/**
 * Mostra una stima del tempo di risoluzione di un ticket basata sullo storico,
 * dato categoria + hotel + area.
 */
const TicketTimeEstimateInline = ({ categoryId, hotelId, area }) => {
  const [loading, setLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);

  useEffect(() => {
    if (!categoryId || !hotelId) {
      setEstimate(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ category_id: categoryId, hotel_id: hotelId });
        if (area) params.append('area', area);
        const { data } = await api.get(`/analytics/ticket-time-estimate?${params}`);
        if (!cancelled) setEstimate(data);
      } catch (_) {
        if (!cancelled) setEstimate(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    // Debounce minimo per non sovraccaricare API durante la digitazione
    const t = setTimeout(load, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [categoryId, hotelId, area]);

  if (!categoryId || !hotelId) return null;
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-500 px-3 py-2 rounded-md bg-slate-50 border border-slate-200">
        <Loader2 className="w-3 h-3 animate-spin" /> Calcolo stima…
      </div>
    );
  }
  if (!estimate || !estimate.sample_size) {
    return null;
  }

  const confidenceStyles = {
    alta: { bg: 'rgba(16, 185, 129, 0.10)', border: '#10b981', text: '#047857', label: 'Stima affidabile' },
    media: { bg: 'rgba(59, 130, 246, 0.10)', border: '#3b82f6', text: '#1e40af', label: 'Stima discreta' },
    bassa: { bg: 'rgba(245, 158, 11, 0.10)', border: '#f59e0b', text: '#b45309', label: 'Stima indicativa' },
    nessuno: { bg: 'rgba(148, 163, 184, 0.10)', border: '#94a3b8', text: '#475569', label: 'Stima generica' },
  };
  const c = confidenceStyles[estimate.confidence] || confidenceStyles.nessuno;

  return (
    <div
      className="px-3 py-2.5 rounded-md border-l-4 flex items-start gap-2"
      style={{ background: c.bg, borderLeftColor: c.border, color: c.text }}
      data-testid="ticket-time-estimate"
    >
      <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="text-xs flex-1 min-w-0">
        <div className="font-bold flex items-center gap-1.5 flex-wrap">
          <Clock className="w-3 h-3" /> Tempo medio storico: <span className="font-extrabold" data-testid="estimate-mean">{estimate.mean_human}</span>
          <span className="opacity-70">·</span>
          <span className="opacity-80">range {estimate.range_human}</span>
        </div>
        <div className="opacity-70 mt-0.5">
          {c.label} · basato su {estimate.sample_size} ticket simili negli ultimi {estimate.months_window} mesi
          {estimate.is_fallback && <span className="ml-1 italic">(media generale, no match esatti)</span>}
        </div>
      </div>
    </div>
  );
};

export default TicketTimeEstimateInline;
